import { useQuery } from "@apollo/client/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { OrderBy } from "~/common/types/graphql";
import {
  GetRecordsForTimelineDocument,
  type GetRecordsForTimelineQuery,
  type GetRecordsForTimelineQueryVariables,
} from "~/features/entities/gqls";
import { useTenant } from "~/features/tenant/context";

const SOURCE_RECORD_PAGE_SIZE = 100;

export function useCompoundEntitySourceRecords(recordIds: readonly string[]) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");

  const recordIdsKey = useMemo(() => recordIds.join(","), [recordIds]);
  const firstRecordIds = useMemo(() => recordIds.slice(0, SOURCE_RECORD_PAGE_SIZE), [recordIds]);
  const [pagination, setPagination] = useState({ key: recordIdsKey, requestedCount: firstRecordIds.length });
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const requestedCount = pagination.key === recordIdsKey ? pagination.requestedCount : firstRecordIds.length;
  const requestKey = `${tenantId}:${recordIdsKey}`;
  const requestGeneration = useRef({ key: requestKey, value: 0 });
  if (requestGeneration.current.key !== requestKey) {
    requestGeneration.current = { key: requestKey, value: requestGeneration.current.value + 1 };
  }

  useEffect(() => {
    setPagination({ key: recordIdsKey, requestedCount: firstRecordIds.length });
    setLoadingMore(false);
    setLoadMoreError(false);
  }, [firstRecordIds.length, recordIdsKey, tenantId]);

  const query = useQuery<GetRecordsForTimelineQuery, GetRecordsForTimelineQueryVariables>(
    GetRecordsForTimelineDocument,
    {
      variables: {
        tenantId,
        limit: firstRecordIds.length,
        where: { id: { _in: [...firstRecordIds] } },
        order_by: { id: "ASC" as OrderBy },
      },
      skip: firstRecordIds.length === 0,
    },
  );

  const loadMore = async () => {
    if (loadingMore || requestedCount >= recordIds.length) return;

    const nextRecordIds = recordIds.slice(requestedCount, requestedCount + SOURCE_RECORD_PAGE_SIZE);
    const requestGenerationAtStart = requestGeneration.current.value;
    setLoadingMore(true);
    setLoadMoreError(false);
    try {
      await query.fetchMore({
        variables: {
          tenantId,
          limit: nextRecordIds.length,
          where: { id: { _in: [...nextRecordIds] } },
          order_by: { id: "ASC" as OrderBy },
        },
        updateQuery: (previous, { fetchMoreResult }) => {
          if (requestGeneration.current.value !== requestGenerationAtStart) return previous;
          return {
            ...previous,
            records: [...previous.records, ...fetchMoreResult.records],
          };
        },
      });
      if (requestGeneration.current.value === requestGenerationAtStart) {
        setPagination({ key: recordIdsKey, requestedCount: requestedCount + nextRecordIds.length });
      }
    } catch {
      if (requestGeneration.current.value === requestGenerationAtStart) {
        setLoadMoreError(true);
      }
    } finally {
      if (requestGeneration.current.value === requestGenerationAtStart) {
        setLoadingMore(false);
      }
    }
  };

  const retry = () => {
    if (loadMoreError) {
      return loadMore();
    }
    return query.refetch();
  };

  const visibleRecordIds = useMemo(() => recordIds.slice(0, requestedCount), [recordIds, requestedCount]);

  return {
    ...query,
    loading: query.loading || loadingMore,
    error: query.error != null || loadMoreError,
    visibleRecordIds,
    hasMore: visibleRecordIds.length < recordIds.length,
    loadMore,
    retry,
  };
}
